import React, { useState } from "react";
import { Cpu, AlertTriangle, CheckCircle2 } from "lucide-react";
import useFireboxLive, { wib, ageMin, rates, mbps, uptimeText } from "./useFireboxLive";

// Jakarta office WatchGuard T80 (NFPIMIDJKTKN01) — stylized device faceplate + port details.
// Data snapshot 2026-10-01 from Fireware Web UI and WSM Firebox System Manager. Not live yet.
const SNAPSHOT = "2026-10-01 16:50 WIB";
const SYS = [
  ["Name", "NFPIMIDJKTKN01"], ["Model", "T80"], ["Fireware", "12.12.2.B745592"], ["Serial", "C03805EDE728D"],
  ["Uptime", "18 days (at snapshot)"], ["Mode", "Mixed Routing"], ["Site", "Jakarta office · Indomas"], ["LAN IP", "10.32.0.17"],
];
const HEALTH = [
  ["warn", "Log Server 10.32.1.21 (Dimension) not connected"],
  ["warn", "WatchGuard Cloud registration failed"],
  ["warn", "3 fault reports"],
  ["warn", "BOVPN KIJING endpoint 1 (58.147.187.26) message retry timeout"],
  ["info", "DNSWatch disabled"],
  ["ok", "SNMP v2c enabled (management station 10.32.8.45)"],
];
// [port, alias, type, ip/mask, gateway, mac, sent, sentPkts, recv, recvPkts, role, status]
const PORTS = [
  [0, "Velo", "External", "222.165.199.102/30", "222.165.199.101", "00:90:7F:1A:4F:B2", "173.8 GB", "491.6 M", "860.0 GB", "890.4 M", "Primary internet · BOVPN to Dumai, Kijing, Medan", "up"],
  [1, "Management WG", "Trusted", "10.0.1.1/24", "-", "00:90:7F:1A:4F:B3", "0 B", "0", "0 B", "0", "Out-of-band management (no link)", "idle"],
  [2, "Neuviz", "External", "10.26.4.178/30", "10.26.4.177", "00:90:7F:1A:4F:B4", "232.8 GB", "246.2 M", "285.2 GB", "350.9 M", "Secondary internet (policy 'Bypass Internet via Neuviz')", "up"],
  [3, "AWS-JKT TUNNEL", "External", "222.165.224.125/29", "222.165.224.121", "00:90:7F:1A:4F:B5", "1.3 GB", "1.5 M", "975.7 MB", "2.0 M", "AWS Jakarta Site-to-Site VPN (bvpn1/bvpn2)", "up"],
  [4, "Link to Cisco", "Disabled", "-", "-", "-", "-", "-", "-", "-", "Disabled", "off"],
  [5, "to coreswitch", "Trusted", "10.32.0.17/23", "-", "00:90:7F:1A:4F:B7", "1.31 TB", "1.44 G", "370.2 GB", "811.6 M", "Jakarta office LAN (core switch)", "up"],
  [6, "AWS Tunnel", "External", "192.168.10.2/24", "192.168.10.1", "00:90:7F:1A:4F:B8", "2.8 GB", "10.5 M", "20.7 GB", "17.5 M", "Tunnel to AWS Singapore (PIL) via Velo MikroTik", "up"],
  [7, "SAP HOST TESTING", "External", "222.165.224.124/29", "222.165.224.121", "00:90:7F:1A:4F:B9", "2.0 GB", "8.7 M", "1.8 GB", "15.9 M", "SAP host testing", "up"],
];
const TYPE_C = { External: "#ef4444", Trusted: "#22c55e", Optional: "#eab308", Disabled: "#64748b" };
const LED = { up: "#22c55e", idle: "#f59e0b", off: "#334155" };

const card = { background: "var(--card-bg, #fff)", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 };
const chip = (c) => ({ display: "inline-block", padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: c, background: c + "1a", border: `1px solid ${c}55`, whiteSpace: "nowrap" });

function Port({ p, x, y, sel, onSel }) {
  const [n, alias, type, , , , , , , , , st] = p;
  const tc = TYPE_C[type];
  return (
    <g onClick={() => onSel(n)} style={{ cursor: "pointer" }}>
      <title>{`eth${n} ${alias} (${type})`}</title>
      <text x={x + 24} y={y - 10} textAnchor="middle" fontSize="9" fill="#cbd5e1">{alias.length > 13 ? alias.slice(0, 12) + "…" : alias}</text>
      <rect x={x - 3} y={y - 3} width={54} height={50} rx={5} fill={sel ? "#0ea5e9" : "transparent"} opacity={sel ? 0.35 : 1} />
      <rect x={x} y={y} width={48} height={44} rx={3} fill="#0b1220" stroke={tc} strokeWidth={2} />
      {/* RJ45 cavity */}
      <path d={`M${x + 8} ${y + 34} V${y + 14} H${x + 16} V${y + 8} H${x + 32} V${y + 14} H${x + 40} V${y + 34} Z`} fill="#111827" stroke="#374151" />
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <rect key={i} x={x + 12 + i * 3.2} y={y + 14} width={1.6} height={5} fill="#a3a3a3" />)}
      <circle cx={x + 7} cy={y + 40} r={3} fill={LED[st]} className={st === "up" ? "wgdev-blink" : ""} />
      <circle cx={x + 41} cy={y + 40} r={3} fill={st === "off" ? LED.off : "#22c55e"} />
      <text x={x + 24} y={y + 60} textAnchor="middle" fontSize="12" fontWeight="700" fill="#f8fafc">{n}</text>
    </g>
  );
}

function Faceplate({ sel, onSel, ports }) {
  const W = 920, H = 170, px = 300, gap = 74;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", minWidth: 620, height: "auto", display: "block" }} role="img" aria-label="WatchGuard T80 Jakarta front panel">
      <style>{`@keyframes wgdevb{0%,100%{opacity:1}50%{opacity:.25}} .wgdev-blink{animation:wgdevb 1.2s infinite}`}</style>
      <defs>
        <linearGradient id="wgdevChassis" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#2b3442" /><stop offset="1" stopColor="#141a23" /></linearGradient>
      </defs>
      <rect x={4} y={10} width={W - 8} height={H - 20} rx={14} fill="url(#wgdevChassis)" stroke="#0f172a" strokeWidth={2} />
      <rect x={4} y={10} width={150} height={H - 20} rx={14} fill="#b91c1c" />
      <rect x={140} y={10} width={14} height={H - 20} fill="#b91c1c" />
      <text x={22} y={62} fontSize="20" fontWeight="800" fill="#fff">Firebox</text>
      <text x={22} y={90} fontSize="26" fontWeight="800" fill="#fff">T80</text>
      <text x={22} y={118} fontSize="10" fill="#fecaca">NFPIMIDJKTKN01</text>
      <text x={22} y={132} fontSize="10" fill="#fecaca">Jakarta · Indomas</text>
      {[["PWR", "#22c55e", true], ["STATUS", "#22c55e", false], ["FAULT", "#f59e0b", false]].map(([l, c, b], i) => (
        <g key={l}>
          <circle cx={186} cy={52 + i * 30} r={5} fill={c} className={b ? "" : "wgdev-blink"} />
          <text x={198} y={56 + i * 30} fontSize="10" fill="#cbd5e1">{l}</text>
        </g>
      ))}
      <rect x={250} y={60} width={26} height={12} rx={2} fill="#0b1220" stroke="#64748b" />
      <text x={263} y={88} textAnchor="middle" fontSize="9" fill="#94a3b8">USB</text>
      {ports.map((p, i) => <Port key={p[0]} p={p} x={px + i * gap} y={58} sel={sel === p[0]} onSel={onSel} />)}
      <text x={px + 3.5 * gap + 24} y={H - 18} textAnchor="middle" fontSize="9" fill="#64748b">1 GbE · click a port for details</text>
    </svg>
  );
}

function PortDetail({ n, ports }) {
  const p = ports.find((x) => x[0] === n);
  const [num, alias, type, ip, gw, mac, sent, sp, recv, rp, role, st, rateTxt, speed] = p;
  const rows = [["IPv4", ip], ["Gateway", gw], ["MAC", mac], ["Sent", sp === "n/a" ? sent : `${sent} (${sp} pkts)`], ["Received", rp === "n/a" ? recv : `${recv} (${rp} pkts)`], ["Role", role]].concat(rateTxt ? [["Throughput", rateTxt]] : []).concat(speed ? [["Link speed", speed]] : []);
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>eth{num} · {alias}</span>
        <span style={chip(TYPE_C[type])}>{type}</span>
        <span style={chip(st === "up" ? "#16a34a" : st === "off" ? "#64748b" : "#d97706")}>{st === "up" ? "Available" : st === "off" ? "Disabled" : "No traffic"}</span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} style={{ borderTop: "1px solid #f1f5f9" }}>
              <td style={{ padding: "4px 8px 4px 0", color: "#64748b", width: 90 }}>{k}</td>
              <td style={{ padding: 4, fontFamily: /IPv4|Gateway|MAC/.test(k) ? "monospace" : "inherit" }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const gb = (b) => { const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0, v = Number(b); while (v >= 1000 && i < 4) { v /= 1000; i++; } return v.toFixed(i ? 1 : 0) + " " + u[i]; };
export default function WgDeviceJakarta() {
  const [sel, setSel] = useState(0);
  const { live, prev, checkedAt, error } = useFireboxLive();
  // Overlay live ifOperStatus + 64-bit counters (sent = ifHCOutOctets, received = ifHCInOctets) on the static port list.
  const RT = rates(live, prev);
  const fmtN = (n) => Number(n).toLocaleString("en-US");
  const P = PORTS.map((p) => {
    const L = live && live.ifs && live.ifs.find((x) => x.eth === p[0]);
    if (!L || p[11] === "off") return p;
    const q = p.slice();
    if (L.oper) q[11] = L.oper === "up" ? (p[11] === "idle" ? "idle" : "up") : "off";
    if (L.out != null) { q[6] = gb(L.out); q[7] = L.outPkts != null ? fmtN(L.outPkts) : "n/a"; }
    if (L.in != null) { q[8] = gb(L.in); q[9] = L.inPkts != null ? fmtN(L.inPkts) : "n/a"; }
    const R = RT[p[0]];
    q[12] = R && (R.inMbps != null || R.outMbps != null) ? `↑ ${mbps(R.outMbps)} · ↓ ${mbps(R.inMbps)} (5-min avg)` : null;
    q[13] = L.speedMbps ? (L.speedMbps >= 1000 ? L.speedMbps / 1000 + " Gbps" : L.speedMbps + " Mbps") : null;
    return q;
  });
  // System card: live values override the static snapshot rows
  const SY = live && live.sys ? live.sys : {};
  const sysRows = SYS.map(([k, v]) => {
    if (k === "Fireware" && SY.version) return [k, SY.version];
    if (k === "Uptime" && live && live.uptime != null) return [k, uptimeText(live.uptime) + " (live)"];
    return [k, v];
  }).concat(live && live.sys ? [["Active conns", SY.activeConns != null ? fmtN(SY.activeConns) : "-"], ["BOVPN SAs", live.active != null ? String(live.active) : "-"], ["Total sent", gb(SY.sentBytes)], ["Total recv", gb(SY.recvBytes)]] : []);
  // Health: live checks first, then items only visible in the Web UI (snapshot)
  const H = [];
  if (live) {
    H.push(ageMin(checkedAt) > 15 ? ["warn", "SNMP data stale (last " + wib(checkedAt) + ")"] : ["ok", "SNMP reachable via agent 10.40.1.58 · " + wib(checkedAt)]);
    const EXPECT_UP = [0, 2, 3, 5, 6, 7];
    const down = (live.ifs || []).filter((f) => EXPECT_UP.includes(f.eth) && f.oper && f.oper !== "up");
    H.push(down.length ? ["warn", "Port down: " + down.map((f) => "eth" + f.eth + " " + f.name).join(", ")] : ["ok", "All in-use ports up (eth0, 2, 3, 5, 6, 7)"]);
    const sas = live.sas || [];
    const kij = new Set(sas.filter((x) => x.peer === "36.95.179.155" || x.peer === "58.147.187.26").map((x) => x.peer));
    if (!kij.size) H.push(["warn", "BOVPN KIJING: no active SA"]);
    else if (!kij.has("58.147.187.26")) H.push(["warn", "BOVPN KIJING running on backup endpoint 2 (36.95.179.155)"]);
    else H.push(["ok", "BOVPN KIJING on endpoint 1 (58.147.187.26)"]);
    const aws = ["16.78.66.179", "108.136.96.201"].filter((p) => sas.some((x) => x.peer === p)).length;
    H.push(aws === 2 ? ["ok", "AWS Jakarta VPN: bvpn1 + bvpn2 up"] : ["warn", "AWS Jakarta VPN: " + aws + "/2 tunnels up"]);
  } else if (error) H.push(["warn", "SNMP live data unavailable: " + error]);
  HEALTH.filter(([, t]) => !(live && /KIJING/.test(t))).forEach(([lv, t]) => H.push([lv, t + (live ? " (Web UI, 1 Oct)" : "")]));
  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <Cpu size={20} color="rgb(var(--brand-600, 13 148 136))" />
        <div style={{ marginRight: "auto" }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>WatchGuard Device — Jakarta</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>Firebox T80 · Fireware 12.12.2 · {live ? <>port status &amp; counters live (SNMP) · checked {wib(checkedAt)}{ageMin(checkedAt) > 15 ? " (stale)" : ""}</> : <>snapshot {SNAPSHOT}{error ? " · live: " + error : ""}</>}</div>
        </div>
        <span style={{ fontSize: 11, color: "#64748b" }}>
          {Object.entries(TYPE_C).map(([k, c]) => <span key={k} style={{ marginLeft: 10 }}><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: c, marginRight: 4, verticalAlign: -1 }} />{k}</span>)}
        </span>
      </div>
      <div style={{ overflowX: "auto", marginBottom: 12 }}><Faceplate sel={sel} onSel={setSel} ports={P} /></div>
      <div className="wgdev-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0,1.3fr) minmax(0,1fr) minmax(0,1fr)", gap: 12 }}>
        <style>{`@media (max-width: 900px){ .wgdev-grid{ grid-template-columns: minmax(0,1fr) !important; } }`}</style>
        <PortDetail n={sel} ports={P} />
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>System</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <tbody>
              {sysRows.map(([k, v]) => (
                <tr key={k} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "4px 8px 4px 0", color: "#64748b", width: 80 }}>{k}</td>
                  <td style={{ padding: 4 }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Health</div>
          {H.map(([lv, t], i) => (
            <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 12, padding: "4px 0", borderTop: i ? "1px solid #f1f5f9" : 0 }}>
              {lv === "ok" ? <CheckCircle2 size={14} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} /> : <AlertTriangle size={14} color={lv === "warn" ? "#d97706" : "#64748b"} style={{ flexShrink: 0, marginTop: 1 }} />}
              <span>{t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
